import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import useOrderApi from '../../hooks/useOrderApi';
import TextField from '../../components/formfields/TextField';
import RichSelectField from '../../components/formfields/RichSelectField';
import PhoneNumberField from '../../components/formfields/PhoneNumberField';
import { useTheme } from '../../context/ThemeContext';
import omit from 'lodash/omit';
import useRailsAuth from '../../hooks/useRailsAuth';
import { useFormStore } from '../../stores/formData';
import ForgotPassword from './ForgotPassword';

const Spinner = function(props) {
  var size = props.size || 16;
  var color = props.color || '#ffffff';
  
  var spinnerStyle = {
    width: size + 'px',
    height: size + 'px',
    border: '2px solid ' + color + '33',
    borderTop: '2px solid ' + color,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  };

  return React.createElement('div', { style: spinnerStyle });
};
Spinner.propTypes = {
  size: PropTypes.number,
  color: PropTypes.string
};

// Country options for the dropdown
const COUNTRY_OPTIONS = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'FR', label: 'France' },
  { value: 'DE', label: 'Germany' },
  { value: 'IT', label: 'Italy' },
  { value: 'ES', label: 'Spain' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'JP', label: 'Japan' },
  { value: 'BR', label: 'Brazil' },
  { value: 'MX', label: 'Mexico' }
];

const extractValue = (data) => {
  // Check if it's a SyntheticBaseEvent
  if (data && typeof data === 'object' && data.target && data.target.value !== undefined) {
    return data.target.value;
  }
  
  // Check if it's a regular object with a value property
  if (data && typeof data === 'object' && data.value !== undefined) {
    return data.value;
  }
  
  // Return as-is if it's a primitive value
  return data;
};

const Authentication = ({ 
  onAuthenticate
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const [loggingIn, setLoggingIn] = useState(false);
  const [signingUp, setSigningUp] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [loginError, setLoginError] = useState(null);
  const [signupError, setSignupError] = useState(null);
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });
  const [authData, setAuthData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    termsAccepted: false,
    countryCode: 'US'
  });

  const actions = useFormStore((state) => state.actions);
  const helpers = useFormStore((state) => state.helpers);
  const customerInfo = useFormStore((state) => state.formData.customerInfo); 
  const brandId = useFormStore((state) => state.formData.brandId);

  const {
    createUser,
    error: apiError
  } = useOrderApi();

  const { 
    login: railsLogin, 
  } = useRailsAuth();

  useEffect(() => {
    if (customerInfo?.authToken?.length) {
      setIsAuthenticated(true);
      actions.form.updateField('customerInfo', customerInfo);
    }
  }, [customerInfo]);

  const handleAuthSubmit = useCallback((authMode) => {
    const performAuth = async () => {
      setIsAuthenticated(false);
      setLoginError(null);
      setSignupError(null);
      actions.form.updateField('customerInfo', {
        ...customerInfo,
        ...omit(authData, ['password', 'confirmPassword'])
      });
      
      let canSignIn = true;

      if(authMode === 'signup') {
        // if sign up try to create the user
        setSigningUp(true);
        try {
          const signupResults = await createUser(
            helpers.getAuthToken(),
            helpers.getBrandId(), {
            email: authData.email,
            firstName: authData.firstName,
            lastName: authData.lastName,
            password: authData.password,
            phoneNumber: authData.phoneNumber,
            termsAccepted: authData.termsAccepted
          });
        } catch (error) {
            const errorText = typeof apiError === 'string' ? 
              JSON.parse(apiError).errors?.[0] : (apiError?.message || '');
            setSignupError('Signup failed. ' + errorText + ' Please check your credentials and try again.');
            canSignIn = false;
        } finally {
          setSigningUp(false);
        }
      }

      if(canSignIn) {
        // login the user
        try {
          setLoggingIn(true);
          const results = await railsLogin(
             authMode === 'login' ? loginData.email : authData.email,
             authMode === 'login' ? loginData.password : authData.password
          );

          const customerInfo = {
            id: results.user.id,
            authToken: results.authToken,
            firstName: results.firstName || results.user.full_name.split(' ')[0] || '',
            lastName: results.lastName || results.user.full_name.split(' ')[1] || '',
            email: results.email || results.user.email,
            phoneNumber: results.phone_number || results.user.phone_number || '',
            role: results.user.role
          }
          actions.form.updateFields({ 
            authToken: results.authToken,
            customerInfo: customerInfo
          });
          setIsAuthenticated(true);
          setLoggingIn(false);
          onAuthenticate?.({
            ...customerInfo,
            authToken: results.authToken
          });
        } catch (error) {
          setLoggingIn(false);
          setLoginError('Authentication failed. Please check your credentials and try again.');
        }
      }
    }
    performAuth()
  }, [authData, loginData, customerInfo, actions]);

  const handleAuthChange = useCallback((authMode, field, value) => {
    const extractedValue = extractValue(value);  
    if(authMode === 'login') {
      setLoginData(prev => ({
        ...prev,
        [field]: extractedValue
      }));
      return;
    }
    // signup mode
    setAuthData(prev => ({
      ...prev,
      [field]: extractedValue
    }));
  }, [loginData, authData]); 

  const handlePasswordReset = useCallback((new_password, user) => {
    const performLogin = async (email, password) => {
      setIsAuthenticated(false);
      setLoginError(null);
      setSignupError(null);
      try {
        const results = await railsLogin(email, password);

        const customerInfo = {
          id: results.user.id,
          authToken: results.authToken,
          firstName: results.firstName || results.user.full_name.split(' ')[0] || '',
          lastName: results.lastName || results.user.full_name.split(' ')[1] || '',
          email: results.email || results.user.email,
          phoneNumber: results.phone_number || results.user.phone_number || '',
          role: results.user.role
        }
        actions.form.updateFields({ 
          authToken: results.authToken,
          customerInfo: customerInfo
        });
        setIsAuthenticated(true);
        setLoggingIn(false);
        onAuthenticate?.({
          ...customerInfo,
          authToken: results.authToken
        });
      } catch (error) {
        setLoggingIn(false);
        setLoginError('Authentication failed. Please check your credentials and try again.');
      }
    };
    performLogin(user.email, new_password);
  }, []);

  const handleCancelPasswordReset = useCallback(() => {
    setIsAuthenticated(false);
    setLoginError(null);
    setSignupError(null);
  }, []);

  const handleReset = useCallback(() => {
    setIsAuthenticated(false)
    setLoginError(null);
    setSignupError(null);
    actions.form.updateFields({
      customerInfo: {
        id: '',
        authToken: '',
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: ''
      },
      authToken: ''
    });

    setAuthData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      phoneNumber: '',
      termsAccepted: false,
      countryCode: 'US'
    });
  }, [actions]);

  const containerClassName = clsx(
    'rounded-lg border transition-all duration-200',
    {
      'bg-slate-800 border-slate-700': isDark,
      'bg-white border-gray-200': !isDark
    }
  );

  const headerClassName = clsx(
    'text-2xl font-semibold mb-2',
    {
      'text-slate-200': isDark,
      'text-gray-900': !isDark
    }
  );

  const subtitleClassName = clsx(
    'mb-6',
    {
      'text-slate-400': isDark,
      'text-gray-600': !isDark
    }
  );

    const loginButtonClassName = clsx(
    'md:w-[200px] py-3 px-4 rounded-lg font-medium transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    {
      'bg-slate-900 hover:bg-slate-700 text-white focus:ring-slate-500': !isDark,
      'bg-slate-700 hover:bg-slate-600 text-white focus:ring-slate-500': isDark
    }
  );

  const buttonClassName = clsx(
    'w-full py-3 px-4 rounded-lg font-medium transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    {
      'bg-slate-900 hover:bg-slate-700 text-white focus:ring-slate-500': !isDark,
      'bg-slate-700 hover:bg-slate-600 text-white focus:ring-slate-500': isDark
    }
  );

  if (isAuthenticated) {
    // Collapsed Authenticated State
    return (
      <div className={clsx(
        'border rounded-lg p-2 md:p-4',
        {
          'bg-green-50 border-green-200': !isDark,
          'bg-green-900/20 border-green-600': isDark
        }
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={clsx(
              'w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center',
              'text-sm font-semibold'
            )}>
              ✓
            </div>
            <div>
              <p className={clsx(
                'font-medium',
                {
                  'text-green-900': !isDark,
                  'text-green-100': isDark
                }
              )}>
                {customerInfo?.firstName} {customerInfo?.lastName}
              </p>
              <p className={clsx(
                'text-sm',
                {
                  'text-green-700': !isDark,
                  'text-green-300': isDark
                }
              )}>
                {customerInfo?.email}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleReset}
            className={clsx(
              'text-sm underline hover:no-underline',
              {
                'text-green-700 hover:text-green-900': !isDark,
                'text-green-300 hover:text-green-100': isDark
              }
            )}
          >
            Change Account
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <div className="p-4 md:p-6">
        {/* Form Content */}
        <div className="flex flex-col md:grid md:grid-cols-5 gap-8">
          {/* Login Section */}
          <div className="md:w-[80%] md:col-span-2">
            <h2 className={headerClassName}>Account login</h2>
            <p className={subtitleClassName}>
              Please sign in to your account to complete your booking.
            </p>

            <form onSubmit={(e) => { e.preventDefault(); handleAuthSubmit('login'); }} className="space-y-4">
              <TextField
                id="login-email"
                label="Email"
                type="email"
                value={loginData.email}
                onChange={(value) => handleAuthChange('login','email', value)}
                // this catches google form fill
                onBlur={(e) => handleAuthChange('login','email', e.target.value.trim())}
                placeholder="Enter your email"
                required
              />
              
              <TextField
                id="login-password"
                label="Password"
                type="password"
                value={loginData.password}
                onChange={(value) => handleAuthChange('login','password', value)}
                // this catches google form fill
                onBlur={(e) => handleAuthChange('login','password', e.target.value.trim())}
                placeholder="Enter your password"
                required
              />

              <button
                type="submit"
                disabled={loggingIn}
                className={loginButtonClassName}
              >
                <div className="flex items-center justify-center gap-2">
                  {loggingIn && <Spinner size={16} color="#ffffff" />}
                  Log In
                </div>
              </button>
              {/* Error Display */}
              {loginError && (
                <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-600">
                  <p className="text-sm text-red-600 dark:text-red-400">{loginError}</p>
                </div>
              )}
            </form>
            <ForgotPassword
              brandId={brandId}
              onPasswordReset={handlePasswordReset}
              onCancel={handleCancelPasswordReset}
            />
          </div>

          {/* Signup Section */}
          <div className="md:pl-6 md:border-l md:col-span-3">
            <h2 className={headerClassName}>Create an account</h2>
            <p className={subtitleClassName}>
              Create your account now to place your order and enjoy full benefits.
            </p>

            <form onSubmit={(e) => { e.preventDefault(); handleAuthSubmit('signup'); }} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  id="signup-firstname"
                  label="First name"
                  value={authData.firstName}
                  onChange={(value) => handleAuthChange('signup', 'firstName', value)}
                  // this catches google form fill
                  onBlur={(e) => handleAuthChange('signup','firstName', e.target.value.trim())}
                  placeholder="Enter first name"
                  required
                />
                
                <TextField
                  id="signup-lastname"
                  label="Last name"
                  value={authData.lastName}
                  onChange={(value) => handleAuthChange('signup', 'lastName', value)}
                  // this catches google form fill
                  onBlur={(e) => handleAuthChange('signup','lastName', e.target.value.trim())}
                  placeholder="Enter last name"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  id="signup-email"
                  label="Email"
                  type="email"
                  value={authData.email}
                  onChange={(value) => handleAuthChange('signup', 'email', value)}
                  // this catches google form fill
                  onBlur={(e) => handleAuthChange('signup','email', e.target.value.trim())}
                  placeholder="Email address"
                  required
                />
                
                <RichSelectField
                  id="signup-country"
                  label="Country of Residence"
                  value={authData.countryCode}
                  onChange={(value) => handleAuthChange('signup', 'countryCode', value)}
                  // this catches google form fill
                  onBlur={(e) => handleAuthChange('signup','countryCode', e.target.value.trim())}
                  options={COUNTRY_OPTIONS}
                  placeholder="Country"
                  required
                />
              </div>

              <PhoneNumberField
                id="signup-phone"
                label="Phone number"
                value={authData.phoneNumber}
                onChange={(value) => handleAuthChange('signup', 'phoneNumber', value)}
                // this catches google form fill
                onBlur={(e) => handleAuthChange('signup','phoneNumber', e.target.value.trim())}
                defaultCountry={authData.countryCode}
                placeholder="555-555-5555"
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  id="signup-password"
                  label="Password"
                  type="password"
                  value={authData.password}
                  onChange={(value) => handleAuthChange('signup', 'password', value)}
                  // this catches google form fill
                  onBlur={(e) => handleAuthChange('signup','password', e.target.value.trim())}
                  placeholder="Enter password"
                  required
                />
                
                <TextField
                  id="signup-confirm-password"
                  label="Confirm Password"
                  type="password"
                  value={authData.confirmPassword}
                  onChange={(value) => handleAuthChange('signup', 'confirmPassword', value)}
                  // this catches google form fill
                  onBlur={(e) => handleAuthChange('signup','confirmPassword', e.target.value.trim())}
                  placeholder="Confirm password"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={signingUp}
                className={buttonClassName}
              >
                <div className="flex items-center justify-center gap-2">
                  {signingUp && <Spinner size={16} color="#ffffff" />}
                  Sign up
                </div>
              </button>
              <div className="flex items-start gap-3 pt-2">
                {/* <input
                  type="checkbox"
                  id="terms"
                  checked={authData.termsAccepted}
                  onChange={(value) => handleAuthChange('termsAccepted', value)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-1"
                  required
                /> */}
                <label htmlFor="terms" className={clsx(
                  'text-sm leading-relaxed',
                  {
                    'text-slate-300': isDark,
                    'text-gray-700': !isDark
                  }
                )}>
                  By creating an account, you agree to our{' '}
                  <a href="/terms" target="_blank" 
                    className="text-slate-600 hover:text-blue-700 underline font-medium">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="/privacy" 
                     target="_blank" 
                     className="text-slate-600 hover:text-blue-700 underline font-medium">
                    Privacy Policy
                  </a>
                </label>
              </div>
            </form>
          </div>
        </div>

        {/* Error Display */}
        {signupError && (
          <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-600">
            <p className="text-sm text-red-600 dark:text-red-400">{signupError}</p>
          </div>
        )}
      </div>
    </div>
  );
};

Authentication.propTypes = {
  onAuthenticate: PropTypes.func.isRequired
};

export default Authentication;